/**
 * Institutions Management Page
 *
 * CRUD for institutions (SUPER_ADMIN only)
 */

import { useState, useEffect } from 'react'
import {
  provisionDemo,
  type ProvisionDemoOutput,
} from '@/application/admin-provisioning/admin-provisioning.service'
import { useNavigate } from 'react-router-dom'
import {
  Building2,
  Plus,
  Search,
  Power,
  PowerOff,
  Trash2,
  Edit,
  Eye,
  Loader2,
  PackagePlus,
  CheckCircle2,
  CreditCard,
  FlaskConical,
  Layers,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import {
  useInstitutions,
  useActivateInstitution,
  useDeactivateInstitution,
  useDeleteInstitution,
  useUpdateInstitution,
  useCatalogoDeModulos,
  useOnboardInstitution,
  useGlobalCatalog,
} from '@/application/institutions'
import { InstitutionFormModal } from '@/presentation/components/admin/InstitutionFormModal'
import { SelectorDeTiposDeEstudio } from '@/presentation/components/admin/SelectorDeTiposDeEstudio'
import { SubscriptionModal } from '@/presentation/components/admin/SubscriptionModal'
import { APP_ROUTES } from '@/config/constants'
import { toast } from '@/lib/toast'
import { getErrorMessage } from '@/lib/api'
import type { Institution } from '@/domain/institution/types'

export default function InstitutionsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingInstitution, setEditingInstitution] = useState<Institution | null>(null)
  const [viewingInstitution, setViewingInstitution] = useState<Institution | null>(null)
  const [deletingInstitution, setDeletingInstitution] = useState<Institution | null>(null)

  /**
   * Seleccion multiple para borrar en lote.
   *
   * Borrar una institucion borra la historia clinica de una clinica entera,
   * asi que el lote es comodo para la basura de demostracion y peligroso para
   * un cliente real. Por eso hay dos barreras: el resumen dice exactamente
   * que se va a borrar, y si en la seleccion hay alguna que NO es de
   * demostracion, hay que escribir ELIMINAR para confirmar.
   */
  const [seleccionadas, setSeleccionadas] = useState<Set<string>>(new Set())
  const [confirmandoLote, setConfirmandoLote] = useState(false)

  /**
   * Modulos de una institucion: lo que tiene contratado.
   *
   * Se edita desde aqui porque es una decision comercial (que se vendio), no
   * clinica. El servidor niega por su cuenta las rutas de un modulo apagado;
   * esta pantalla solo decide cuales estan encendidos.
   */
  const [modulosDe, setModulosDe] = useState<Institution | null>(null)
  const [textoConfirmacion, setTextoConfirmacion] = useState('')
  const [borrandoLote, setBorrandoLote] = useState(false)
  const [onboardingInstitution, setOnboardingInstitution] = useState<Institution | null>(null)
  const [subscriptionInstitution, setSubscriptionInstitution] = useState<Institution | null>(null)
  const [selectedStudyTypes, setSelectedStudyTypes] = useState<Set<string>>(new Set())
  const [selectedPriorities, setSelectedPriorities] = useState<Set<string>>(new Set())
  const [creandoDemo, setCreandoDemo] = useState(false)
  const [eligiendoDemo, setEligiendoDemo] = useState(false)
  const [modalidadesDemo, setModalidadesDemo] = useState<Set<string>>(new Set())
  const [entornoDemo, setEntornoDemo] = useState<ProvisionDemoOutput | null>(null)
  const [onboardResult, setOnboardResult] = useState<{ studyTypesCopied: number; prioritiesCopied: number; skipped: number } | null>(null)

  const { data, isLoading, refetch } = useInstitutions({
    search: search || undefined,
    limit: 50,
  })

  const activateMutation = useActivateInstitution()
  const deactivateMutation = useDeactivateInstitution()
  const deleteMutation = useDeleteInstitution()
  const updateMutation = useUpdateInstitution()
  const { data: catalogoModulos } = useCatalogoDeModulos()
  const onboardMutation = useOnboardInstitution()
  const catalogQuery = useGlobalCatalog(!!onboardingInstitution || eligiendoDemo)

  // Pre-select all when catalog loads for the first time on this dialog open
  useEffect(() => {
    if (catalogQuery.data && onboardingInstitution && selectedStudyTypes.size === 0 && selectedPriorities.size === 0) {
      setSelectedStudyTypes(new Set(catalogQuery.data.studyTypes.map((s) => s.code)))
      setSelectedPriorities(new Set(catalogQuery.data.priorities.map((p) => p.code)))
    }
  }, [catalogQuery.data, onboardingInstitution])

  useEffect(() => {
    if (catalogQuery.data && eligiendoDemo && modalidadesDemo.size === 0) {
      setModalidadesDemo(new Set(catalogQuery.data.studyTypes.map((s) => s.code)))
    }
  }, [catalogQuery.data, eligiendoDemo])

  const crearEntornoDemo = async () => {
    setCreandoDemo(true)
    try {
      const resultado = await provisionDemo({
        studyTypeCodes: modalidadesDemo.size > 0 ? Array.from(modalidadesDemo) : undefined,
      })
      setEntornoDemo(resultado)
      setEligiendoDemo(false)
      await refetch()
    } catch (err) {
      toast.error(getErrorMessage(err, 'No se pudo crear el entorno de demostración'))
    } finally {
      setCreandoDemo(false)
    }
  }

  const handleToggleActive = (institution: Institution) => {
    if (institution.isActive) {
      deactivateMutation.mutate(institution.id, {
        onSuccess: () => toast.success('Institución desactivada'),
        onError: (err) => toast.error(getErrorMessage(err, 'Error al desactivar institución')),
      })
    } else {
      activateMutation.mutate(institution.id, {
        onSuccess: () => toast.success('Institución activada'),
        onError: (err) => toast.error(getErrorMessage(err, 'Error al activar institución')),
      })
    }
  }

  const handleOnboard = () => {
    if (!onboardingInstitution) return
    onboardMutation.mutate(
      {
        id: onboardingInstitution.id,
        data: {
          copyStudyTypes: selectedStudyTypes.size > 0,
          copyPriorities: selectedPriorities.size > 0,
          studyTypeCodes: selectedStudyTypes.size > 0 ? Array.from(selectedStudyTypes) : undefined,
          priorityCodes: selectedPriorities.size > 0 ? Array.from(selectedPriorities) : undefined,
        },
      },
      {
        onSuccess: (result) => {
          setOnboardResult(result)
          toast.success('Institución aprovisionada correctamente')
        },
        onError: (err) => toast.error(getErrorMessage(err, 'Error al aprovisionar institución')),
      }
    )
  }

  const esDeDemostracion = (i: Institution) =>
    i.name.startsWith('DEMO ') || (i.slug ?? '').startsWith('demo-')

  const institucionesSeleccionadas = (data?.institutions ?? []).filter((i) =>
    seleccionadas.has(i.id)
  )
  const hayRealesEnLaSeleccion = institucionesSeleccionadas.some((i) => !esDeDemostracion(i))
  const puedeConfirmarLote =
    !hayRealesEnLaSeleccion || textoConfirmacion.trim().toUpperCase() === 'ELIMINAR'

  const alternarSeleccion = (id: string) => {
    setSeleccionadas((previas) => {
      const nuevas = new Set(previas)
      if (nuevas.has(id)) nuevas.delete(id)
      else nuevas.add(id)
      return nuevas
    })
  }

  const seleccionarDemostraciones = () => {
    const demos = (data?.institutions ?? []).filter(esDeDemostracion).map((i) => i.id)
    setSeleccionadas(new Set(demos))
  }

  const eliminarSeleccionadas = async () => {
    setBorrandoLote(true)
    let eliminadas = 0
    const fallidas: string[] = []

    // Una a una y en serie: el borrado arrastra datos relacionados y lanzarlas
    // todas a la vez solo hace que un fallo sea imposible de atribuir.
    for (const institucion of institucionesSeleccionadas) {
      try {
        await deleteMutation.mutateAsync(institucion.id)
        eliminadas++
      } catch {
        fallidas.push(institucion.name)
      }
    }

    setBorrandoLote(false)
    setConfirmandoLote(false)
    setTextoConfirmacion('')
    setSeleccionadas(new Set())

    if (eliminadas > 0) toast.success(`${eliminadas} institución(es) eliminada(s)`)
    if (fallidas.length > 0) {
      toast.error(`No se pudieron eliminar: ${fallidas.join(', ')}`)
    }
  }

  const handleDelete = () => {
    if (deletingInstitution) {
      deleteMutation.mutate(deletingInstitution.id, {
        onSuccess: () => {
          toast.success('Institución eliminada correctamente')
          setDeletingInstitution(null)
        },
        onError: (err) => {
          toast.error(getErrorMessage(err, 'Error al eliminar la institución'))
        },
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Instituciones</h1>
          <p className="text-[var(--foreground-muted)]">
            Gestión de instituciones del sistema
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCreateModal(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nueva (rapida)
          </Button>
          <Button variant="outline" onClick={() => setEligiendoDemo(true)}>
            <FlaskConical className="mr-2 h-4 w-4" />
            Entorno de demostración
          </Button>
          <Button onClick={() => navigate(APP_ROUTES.SUPER_ADMIN.INSTITUTION_NEW)}>
            <PackagePlus className="mr-2 h-4 w-4" />
            Aprovisionar IPS
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--foreground-muted)]" />
              <Input
                placeholder="Buscar institución..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="text-sm text-[var(--foreground-muted)]">
              {data?.total ?? 0} instituciones
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <>
            {/* Atajo para el caso real: las de demostracion se acumulan con el
                uso y limpiarlas una a una es un castigo. */}
            {seleccionadas.size === 0 &&
              (data?.institutions ?? []).some(esDeDemostracion) && (
                <div className="border-b border-[var(--border)] px-4 py-2">
                  <Button variant="ghost" size="sm" onClick={seleccionarDemostraciones}>
                    Seleccionar las de demostración
                  </Button>
                </div>
              )}

            {/* Barra del lote: solo aparece cuando hay algo seleccionado, para
                no ocupar sitio el resto del tiempo. */}
            {seleccionadas.size > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-3">
                <p className="text-sm">
                  <span className="font-medium">{seleccionadas.size}</span> seleccionada(s)
                  {hayRealesEnLaSeleccion && (
                    <span className="ml-2 text-[var(--destructive)]">
                      · incluye instituciones que no son de demostración
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSeleccionadas(new Set())}>
                    Quitar selección
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setConfirmandoLote(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Eliminar seleccionadas
                  </Button>
                </div>
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[42px]">
                    <input
                      type="checkbox"
                      aria-label="Seleccionar todas"
                      className="h-4 w-4 cursor-pointer"
                      checked={
                        (data?.institutions.length ?? 0) > 0 &&
                        seleccionadas.size === data?.institutions.length
                      }
                      onChange={(e) =>
                        setSeleccionadas(
                          e.target.checked
                            ? new Set((data?.institutions ?? []).map((i) => i.id))
                            : new Set()
                        )
                      }
                    />
                  </TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Ciudad</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[180px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.institutions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Building2 className="mx-auto h-12 w-12 text-[var(--foreground-muted)] opacity-50" />
                      <p className="mt-2 text-[var(--foreground-muted)]">
                        No hay instituciones
                      </p>
                    </TableCell>
                  </TableRow>
                ) : (
                  data?.institutions.map((institution) => (
                    <TableRow key={institution.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          aria-label={`Seleccionar ${institution.name}`}
                          className="h-4 w-4 cursor-pointer"
                          checked={seleccionadas.has(institution.id)}
                          onChange={() => alternarSeleccion(institution.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {institution.name}
                      </TableCell>
                      <TableCell className="text-[var(--foreground-muted)] font-mono text-sm">
                        {institution.slug}
                      </TableCell>
                      <TableCell>{institution.city || '-'}</TableCell>
                      <TableCell>{institution.email || '-'}</TableCell>
                      <TableCell>
                        <Badge variant={institution.isActive ? 'default' : 'secondary'}>
                          {institution.isActive ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              navigate(APP_ROUTES.SUPER_ADMIN.INSTITUTION_DETAIL(institution.id))
                            }
                            title="Ver detalles"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingInstitution(institution)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setModulosDe(institution)}
                            title="Módulos contratados"
                          >
                            <Layers className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleActive(institution)}
                            title={institution.isActive ? 'Desactivar' : 'Activar'}
                            disabled={activateMutation.isPending || deactivateMutation.isPending}
                          >
                            {institution.isActive ? (
                              <PowerOff className="h-4 w-4" />
                            ) : (
                              <Power className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setOnboardResult(null)
                              setSelectedStudyTypes(new Set())
                              setSelectedPriorities(new Set())
                              setOnboardingInstitution(institution)
                            }}
                            title="Aprovisionar catálogo"
                          >
                            <PackagePlus className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSubscriptionInstitution(institution)}
                            title="Suscripción y pagos"
                          >
                            <CreditCard className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingInstitution(institution)}
                            title="Eliminar"
                            className="text-[var(--destructive)] hover:text-[var(--destructive)]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <InstitutionFormModal
        open={showCreateModal || !!editingInstitution}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setShowCreateModal(false)
            setEditingInstitution(null)
          }
        }}
        institution={editingInstitution}
      />

      {/* View Details Dialog */}
      <Dialog
        open={!!viewingInstitution}
        onOpenChange={(open: boolean) => !open && setViewingInstitution(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{viewingInstitution?.name}</DialogTitle>
            <DialogDescription>Detalles de la institución</DialogDescription>
          </DialogHeader>
          {viewingInstitution && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[var(--foreground-muted)]">Slug</p>
                  <p className="font-mono">{viewingInstitution.slug}</p>
                </div>
                <div>
                  <p className="text-[var(--foreground-muted)]">NIT</p>
                  <p>{viewingInstitution.nit || '-'}</p>
                </div>
                <div>
                  <p className="text-[var(--foreground-muted)]">Ciudad</p>
                  <p>{viewingInstitution.city || '-'}</p>
                </div>
                <div>
                  <p className="text-[var(--foreground-muted)]">Teléfono</p>
                  <p>{viewingInstitution.phone || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[var(--foreground-muted)]">Dirección</p>
                  <p>{viewingInstitution.address || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-[var(--foreground-muted)]">Email</p>
                  <p>{viewingInstitution.email || '-'}</p>
                </div>
                <div>
                  <p className="text-[var(--foreground-muted)]">Estado</p>
                  <Badge variant={viewingInstitution.isActive ? 'default' : 'secondary'}>
                    {viewingInstitution.isActive ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>
                <div>
                  <p className="text-[var(--foreground-muted)]">Creada</p>
                  <p>{new Date(viewingInstitution.createdAt).toLocaleDateString('es-CO')}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setViewingInstitution(null)}>
              Cerrar
            </Button>
            <Button
              onClick={() => {
                setEditingInstitution(viewingInstitution)
                setViewingInstitution(null)
              }}
            >
              Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding Dialog */}
      <Dialog
        open={!!onboardingInstitution}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setOnboardingInstitution(null)
            setOnboardResult(null)
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Aprovisionar institución</DialogTitle>
            <DialogDescription>
              Selecciona los insumos del catálogo global que se copiarán a{' '}
              <strong>{onboardingInstitution?.name}</strong>. Los registros ya existentes
              se omiten automáticamente.
            </DialogDescription>
          </DialogHeader>

          {onboardResult ? (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Aprovisionamiento completado</span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center text-sm">
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-bold">{onboardResult.studyTypesCopied}</p>
                  <p className="text-[var(--foreground-muted)]">Tipos de estudio</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-bold">{onboardResult.prioritiesCopied}</p>
                  <p className="text-[var(--foreground-muted)]">Prioridades</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-2xl font-bold">{onboardResult.skipped}</p>
                  <p className="text-[var(--foreground-muted)]">Omitidos</p>
                </div>
              </div>
              <p className="text-xs text-[var(--foreground-muted)]">
                Estos registros son propios de esta institución y pueden personalizarse
                desde su panel de administración sin afectar a otras IPS.
              </p>
              <DialogFooter>
                <Button onClick={() => setOnboardingInstitution(null)}>Cerrar</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              {catalogQuery.isLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
                </div>
              ) : (
                <div className="mt-4 space-y-5 max-h-[60vh] overflow-y-auto pr-1">

                  <SelectorDeTiposDeEstudio
                    tipos={catalogQuery.data?.studyTypes ?? []}
                    seleccionados={selectedStudyTypes}
                    onCambiar={setSelectedStudyTypes}
                  />

                  {/* Prioridades */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">Prioridades</p>
                      <div className="flex gap-3 text-xs text-[var(--foreground-muted)]">
                        <button
                          type="button"
                          className="hover:text-[var(--foreground)] underline"
                          onClick={() => setSelectedPriorities(new Set(catalogQuery.data?.priorities.map((p) => p.code)))}
                        >
                          Todos
                        </button>
                        <button
                          type="button"
                          className="hover:text-[var(--foreground)] underline"
                          onClick={() => setSelectedPriorities(new Set())}
                        >
                          Ninguno
                        </button>
                        <span className="text-[var(--foreground-muted)]">
                          {selectedPriorities.size}/{catalogQuery.data?.priorities.length ?? 0} seleccionados
                        </span>
                      </div>
                    </div>
                    <div className="rounded-lg border divide-y">
                      {catalogQuery.data?.priorities.map((p) => (
                        <label
                          key={p.code}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[var(--muted)] transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPriorities.has(p.code)}
                            onChange={(e) => {
                              setSelectedPriorities((prev) => {
                                const next = new Set(prev)
                                if (e.target.checked) {
                                  next.add(p.code)
                                } else {
                                  next.delete(p.code)
                                }
                                return next
                              })
                            }}
                            className="h-4 w-4 rounded flex-shrink-0"
                          />
                          <span
                            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium flex-shrink-0"
                            style={{ backgroundColor: p.color, color: p.textColor }}
                          >
                            {p.name}
                          </span>
                          {p.isDefault && (
                            <span className="text-xs text-[var(--foreground-muted)]">— default</span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>

                </div>
              )}

              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setOnboardingInstitution(null)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleOnboard}
                  disabled={
                    onboardMutation.isPending ||
                    catalogQuery.isLoading ||
                    (selectedStudyTypes.size === 0 && selectedPriorities.size === 0)
                  }
                >
                  {onboardMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Aprovisionando...
                    </>
                  ) : (
                    `Aprovisionar ${selectedStudyTypes.size + selectedPriorities.size} elementos`
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingInstitution}
        onOpenChange={(open: boolean) => !open && setDeletingInstitution(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar institución</DialogTitle>
            <DialogDescription>
              ¿Está seguro que desea eliminar la institución{' '}
              <strong>{deletingInstitution?.name}</strong>? Esta acción no se puede
              deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeletingInstitution(null)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modulos contratados por la institucion. */}
      <Dialog open={!!modulosDe} onOpenChange={(abierto) => !abierto && setModulosDe(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Módulos de {modulosDe?.name}</DialogTitle>
            <DialogDescription>
              Lo que esta institución tiene contratado. Lo que se apaga deja de
              aparecer en su menú y el servidor deja de atenderlo.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {(catalogoModulos ?? []).map((modulo) => {
              const ajustes = (modulosDe?.settings ?? {}) as { modulos?: Record<string, boolean> }
              // Lo que la institución no ha declarado toma el valor por
              // defecto QUE VIENE EN EL CATÁLOGO del servidor. Antes esta
              // pantalla repetía la regla por su cuenta ("lo que no está
              // declarado está habilitado") y mostraba como contratado un
              // módulo que nace apagado.
              const habilitado = ajustes.modulos?.[modulo.codigo] ?? modulo.porDefecto

              return (
                <div
                  key={modulo.codigo}
                  className="flex items-start justify-between gap-3 rounded-lg border border-[var(--border)] p-3"
                >
                  <div>
                    <p className="font-medium">{modulo.nombre}</p>
                    <p className="text-sm text-[var(--foreground-muted)]">{modulo.descripcion}</p>
                  </div>

                  {/* Interruptor explicito en vez de una casilla dentro de una
                      etiqueta: ahi el clic se reenviaba y acababa anulandose. */}
                  <Button
                    type="button"
                    size="sm"
                    variant={habilitado ? 'default' : 'outline'}
                    disabled={updateMutation.isPending}
                    aria-pressed={habilitado}
                    aria-label={`${habilitado ? 'Deshabilitar' : 'Habilitar'} ${modulo.nombre}`}
                    className="flex-shrink-0"
                    onClick={() => {
                      if (!modulosDe) return
                      const nuevos = {
                        ...(ajustes.modulos ?? {}),
                        [modulo.codigo]: !habilitado,
                      }

                      updateMutation.mutate(
                        {
                          id: modulosDe.id,
                          data: { settings: { ...ajustes, modulos: nuevos } } as never,
                        },
                        {
                          onSuccess: () => {
                            setModulosDe({
                              ...modulosDe,
                              settings: { ...ajustes, modulos: nuevos },
                            } as Institution)
                            toast.success(
                              !habilitado
                                ? `${modulo.nombre} habilitado`
                                : `${modulo.nombre} deshabilitado`
                            )
                          },
                          onError: (err) =>
                            toast.error(getErrorMessage(err, 'No se pudo cambiar el módulo')),
                        }
                      )
                    }}
                  >
                    {habilitado ? 'Habilitado' : 'Deshabilitado'}
                  </Button>
                </div>
              )
            })}
          </div>

          <p className="mt-2 rounded-lg bg-[var(--muted,#f5f5f5)] p-3 text-xs text-[var(--foreground-muted)]">
            Pacientes, estudios e informes no se pueden apagar: son el producto.
          </p>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setModulosDe(null)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Borrado en lote.
          El resumen dice exactamente que se va a borrar: una lista de nombres
          evita el "creia que habia seleccionado otras". Y si hay alguna que no
          es de demostracion, hay que escribir ELIMINAR: borrar la historia
          clinica de una IPS real no puede estar a un clic de distancia. */}
      <Dialog open={confirmandoLote} onOpenChange={(abierto) => {
        setConfirmandoLote(abierto)
        if (!abierto) setTextoConfirmacion('')
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Eliminar {institucionesSeleccionadas.length} institución(es)
            </DialogTitle>
            <DialogDescription>
              Se eliminarán junto con sus pacientes, estudios e informes. Esta
              acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>

          <ul className="mt-2 max-h-52 overflow-y-auto rounded-md border border-[var(--border)] p-3 text-sm">
            {institucionesSeleccionadas.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 py-1">
                <span>{i.name}</span>
                {!esDeDemostracion(i) && (
                  <span className="text-xs font-medium text-[var(--destructive)]">
                    no es de demostración
                  </span>
                )}
              </li>
            ))}
          </ul>

          {hayRealesEnLaSeleccion && (
            <div className="mt-3 space-y-2">
              <p className="text-sm text-[var(--destructive)]">
                La selección incluye instituciones que no son de demostración.
                Escriba <strong>ELIMINAR</strong> para confirmar.
              </p>
              <input
                type="text"
                value={textoConfirmacion}
                onChange={(e) => setTextoConfirmacion(e.target.value)}
                placeholder="ELIMINAR"
                className="w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
              />
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setConfirmandoLote(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={eliminarSeleccionadas}
              disabled={borrandoLote || !puedeConfirmarLote}
            >
              {borrandoLote ? 'Eliminando...' : 'Eliminar seleccionadas'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={eligiendoDemo} onOpenChange={(open) => !open && setEligiendoDemo(false)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Entorno de demostración</DialogTitle>
            <DialogDescription>
              Las modalidades que elija aquí son las que tendrá la institución, y de
              ellas salen los estudios precargados.
            </DialogDescription>
          </DialogHeader>

          {catalogQuery.isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <div className="mt-2 max-h-[55vh] overflow-y-auto pr-1">
              <SelectorDeTiposDeEstudio
                tipos={catalogQuery.data?.studyTypes ?? []}
                seleccionados={modalidadesDemo}
                onCambiar={setModalidadesDemo}
                titulo="Modalidades de la institución"
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEligiendoDemo(false)}>
              Cancelar
            </Button>
            <Button onClick={crearEntornoDemo} disabled={creandoDemo || modalidadesDemo.size === 0}>
              {creandoDemo && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Crear entorno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credenciales del entorno de demostracion.
          Se muestran una sola vez: el aprovisionamiento no las vuelve a
          exponer, igual que con una institucion real. */}
      <Dialog open={!!entornoDemo} onOpenChange={(open) => !open && setEntornoDemo(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Entorno de demostración creado</DialogTitle>
            <DialogDescription>{entornoDemo?.message}</DialogDescription>
          </DialogHeader>

          {entornoDemo && (
            <div className="space-y-4">
              <div className="rounded-lg border border-[var(--border)] p-4">
                <p className="font-medium">{entornoDemo.institution.name}</p>
                <p className="text-sm text-[var(--foreground-muted)]">
                  {entornoDemo.resumen.pacientes} pacientes ·{' '}
                  {entornoDemo.resumen.estudios} estudios ·{' '}
                  {entornoDemo.resumen.informes} informes
                </p>
              </div>

              <div>
                <p className="mb-2 text-sm font-medium">Cuentas, una por rol</p>
                <div className="overflow-hidden rounded-lg border border-[var(--border)]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rol</TableHead>
                        <TableHead>Correo</TableHead>
                        <TableHead>Contraseña</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entornoDemo.cuentas.map((cuenta) => (
                        <TableRow key={cuenta.email}>
                          <TableCell>
                            <Badge variant="outline">{cuenta.role}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{cuenta.email}</TableCell>
                          <TableCell className="font-mono text-xs">{cuenta.password}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <p className="rounded-lg bg-[var(--warning-background,#fff7ed)] p-3 text-sm">
                {entornoDemo.advertencia}
              </p>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => {
                if (!entornoDemo) return
                const texto = entornoDemo.cuentas
                  .map((c) => `${c.role}\t${c.email}\t${c.password}`)
                  .join('\n')
                void navigator.clipboard.writeText(
                  `${entornoDemo.institution.name}\n\n${texto}`
                )
                toast.success('Credenciales copiadas')
              }}
            >
              Copiar credenciales
            </Button>
            <Button onClick={() => setEntornoDemo(null)}>Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Subscription / Payments Modal */}
      <SubscriptionModal
        institution={subscriptionInstitution}
        open={!!subscriptionInstitution}
        onOpenChange={(open) => !open && setSubscriptionInstitution(null)}
      />
    </div>
  )
}
